import { Service, ServiceGroup } from '@/models/service'
import {
  hasConfiguredMaximum,
  hasConfiguredMinimum,
  UNLIMITED_LIMIT_THRESHOLD,
} from '@/utils/getReservationConstraintMessage'

export type TicketGroupLimitContext = 'spot' | 'reservation'

export function hasConfiguredTicketMaximum(
  availableQuantity?: number
): availableQuantity is number {
  return (
    typeof availableQuantity === 'number' &&
    availableQuantity > 0 &&
    availableQuantity < UNLIMITED_LIMIT_THRESHOLD
  )
}

export function getTicketConfiguredMinimum(service: Service): number | null {
  return hasConfiguredMinimum(service.minimumQuantity)
    ? service.minimumQuantity
    : null
}

export function getTicketConfiguredMaximum(service: Service): number | null {
  return hasConfiguredTicketMaximum(service.availableQuantity)
    ? service.availableQuantity
    : null
}

function getGroupMinimum(
  group:
    | Pick<ServiceGroup, 'minimumReservationQuantity' | 'minimumSpotQuantity'>
    | undefined,
  context: TicketGroupLimitContext
): number {
  if (!group) {
    return 0
  }

  return context === 'spot'
    ? group.minimumSpotQuantity
    : group.minimumReservationQuantity
}

function getGroupMaximum(
  group:
    | Pick<ServiceGroup, 'maximumReservationQuantity' | 'maximumSpotQuantity'>
    | undefined,
  context: TicketGroupLimitContext
): number | null {
  if (!group) {
    return null
  }

  const quantity =
    context === 'spot'
      ? group.maximumSpotQuantity
      : group.maximumReservationQuantity

  return hasConfiguredMaximum(quantity) ? quantity : null
}

export function isTicketMinimumRelevant(
  ticketMinimum: number,
  group:
    | Pick<ServiceGroup, 'minimumReservationQuantity' | 'minimumSpotQuantity'>
    | undefined,
  context: TicketGroupLimitContext
): boolean {
  const groupMinimum = getGroupMinimum(group, context)

  if (groupMinimum <= 0) {
    return true
  }

  return ticketMinimum > groupMinimum
}

export function isTicketMaximumRelevant(
  ticketMaximum: number,
  group:
    | Pick<ServiceGroup, 'maximumReservationQuantity' | 'maximumSpotQuantity'>
    | undefined,
  context: TicketGroupLimitContext
): boolean {
  const groupMaximum = getGroupMaximum(group, context)

  if (groupMaximum === null) {
    return true
  }

  return ticketMaximum < groupMaximum
}

export function getTicketDisplayMinimum(
  service: Service,
  group:
    | Pick<ServiceGroup, 'minimumReservationQuantity' | 'minimumSpotQuantity'>
    | undefined,
  context: TicketGroupLimitContext = 'spot'
): number | null {
  const configuredMinimum = getTicketConfiguredMinimum(service)

  if (configuredMinimum === null) {
    return null
  }

  return isTicketMinimumRelevant(configuredMinimum, group, context)
    ? configuredMinimum
    : null
}

export function getTicketDisplayMaximum(
  service: Service,
  group:
    | Pick<ServiceGroup, 'maximumReservationQuantity' | 'maximumSpotQuantity'>
    | undefined,
  context: TicketGroupLimitContext = 'spot'
): number | null {
  const configuredMaximum = getTicketConfiguredMaximum(service)

  if (configuredMaximum === null) {
    return null
  }

  return isTicketMaximumRelevant(configuredMaximum, group, context)
    ? configuredMaximum
    : null
}
